import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { X, Send, Pencil, CheckCircle2 } from "lucide-react";
import { StatusPill } from "@/components/PortalShell";
import Lightbox from "@/components/Lightbox";
import ReportForm from "@/components/ReportForm";

export default function ReportDetail({ reportId, canManage, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [lb, setLb] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showChecklist, setShowChecklist] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/reports/${reportId}`); setR(data); }
    catch (e) { toast.error(apiErr(e)); onClose(); }
  }, [reportId, onClose]);
  useEffect(() => { load(); }, [load]);

  const resend = async () => { setBusy(true); try { await api.post(`/reports/${reportId}/resend`); toast.success("Report re-sent to client"); } catch (e) { toast.error(apiErr(e)); } setBusy(false); };
  const setStatus = async (status) => { try { await api.patch(`/reports/${reportId}`, { status }); toast.success("Status updated"); load(); onChanged && onChanged(); } catch (e) { toast.error(apiErr(e)); } };

  const photos = (r?.photos || []).map((p) => (typeof p === "string" ? p : p.url));
  const groups = (r?.checklist || []).reduce((acc, c) => { (acc[c.group || "Checklist"] = acc[c.group || "Checklist"] || []).push(c); return acc; }, {});
  const total = (r?.checklist || []).length;
  const notChecked = (r?.checklist || []).filter((c) => c.status === "Not checked").length;
  const flagged = (r?.checklist || []).filter((c) => c.status === "Needs attention").length;
  const inspected = total - notChecked;
  const meters = r?.meters || {};
  const sec = r?.security || {};

  return (
    <div onClick={onClose} data-testid="report-detail" style={{ position: "fixed", inset: 0, zIndex: 170, background: "rgba(20,17,14,.55)", display: "grid", placeItems: "center", padding: 16, overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} className="card-soft" style={{ padding: 24, width: "100%", maxWidth: 640, maxHeight: "92vh", overflowY: "auto" }}>
        {!r ? <p style={{ color: "var(--muted)" }}>Loading…</p> : editing ? (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div className="lbl">Edit report</div>
              <button onClick={() => setEditing(false)} style={{ background: "none", border: "none", cursor: "pointer" }}><X size={18} /></button>
            </div>
            <ReportForm pid={r.property_id} reportId={r.report_id} initial={r} onSaved={() => { setEditing(false); load(); onChanged && onChanged(); }} />
          </div>
        ) : (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 12 }}>
              <div>
                <span className="lbl-m" style={{ color: "var(--olive)" }}>Visit report</span>
                <h2 style={{ fontSize: 26, margin: "4px 0" }}>{r.title}</h2>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}><StatusPill status={r.status} />{r.visit_date && <span style={{ fontSize: 12, color: "var(--muted)" }}>{r.visit_date}</span>}</div>
              </div>
              <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }} data-testid="report-detail-close"><X size={20} /></button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(110px,1fr))", gap: 10, marginTop: 14, background: "var(--alt)", border: "1px solid var(--line)", borderRadius: 4, padding: 14 }} data-testid="report-trust-summary">
              {[["Condition", r.condition || "Good"], ["Checkpoints", `${inspected} of ${total}`], ["Flagged", flagged ? String(flagged) : "None"], ...(r.next_visit ? [["Next visit", r.next_visit]] : [])].map(([l, v]) => (
                <div key={l}><div className="lbl-m" style={{ color: "var(--muted)" }}>{l}</div><div className="font-serif-c" style={{ fontSize: 20, color: l === "Flagged" && flagged ? "#8a5a1f" : "var(--ink)" }}>{v}</div></div>
              ))}
            </div>
            {(r.arrival_time || r.departure_time) && <p style={{ fontSize: 12, color: "var(--muted)", marginTop: 8 }}>On site {r.arrival_time || "-"} to {r.departure_time || "-"} · Inspected by {r.author}</p>}

            <p style={{ fontSize: 15, marginTop: 14 }}>{r.summary}</p>
            {r.recommendations && <div style={{ background: "var(--alt)", padding: 12, borderRadius: 2, marginTop: 10, fontSize: 14 }}><span className="lbl-m">Recommendations</span><p style={{ marginTop: 4 }}>{r.recommendations}</p></div>}

            {(meters.electricity || meters.water || meters.gas) && (
              <div style={{ marginTop: 16 }}><span className="lbl-m">Meter readings</span>
                <div style={{ display: "flex", gap: 20, marginTop: 6, flexWrap: "wrap", fontSize: 14 }}>
                  {meters.electricity && <span>Electricity: <b>{meters.electricity}</b></span>}
                  {meters.water && <span>Water: <b>{meters.water}</b></span>}
                  {meters.gas && <span>Gas: <b>{meters.gas}</b></span>}
                </div>
              </div>
            )}

            {(("alarm_set" in sec) || ("doors_locked" in sec) || ("keys_returned" in sec) || sec.note) && (
              <div style={{ marginTop: 16 }}><span className="lbl-m">Security &amp; keyholding</span>
                <div style={{ display: "grid", gap: 5, marginTop: 6, fontSize: 14 }}>
                  {[["alarm_set", "Alarm set on leaving"], ["doors_locked", "All doors & windows secured"], ["keys_returned", "Keys returned to coded safe"]].map(([k, lbl]) => (k in sec) && (
                    <div key={k} style={{ display: "flex", alignItems: "center", gap: 8 }}><CheckCircle2 size={15} color={sec[k] ? "#5B6440" : "#c68a3a"} /> <span style={{ color: sec[k] ? "var(--ink)" : "var(--muted)" }}>{lbl}</span></div>
                  ))}
                  {sec.note && <em style={{ color: "var(--muted)" }}>{sec.note}</em>}
                </div>
              </div>
            )}

            {Object.keys(groups).length > 0 && (
              <div style={{ marginTop: 16 }}>
                <button className="link-u" onClick={() => setShowChecklist((s) => !s)} data-testid="toggle-checklist" style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}>{showChecklist ? "Hide" : "View"} full inspection checklist ({total} checkpoints)</button>
                {showChecklist && Object.entries(groups).map(([g, items]) => (
                  <div key={g} style={{ marginTop: 10 }}>
                    <div className="lbl-m" style={{ color: "var(--honey)" }}>{g}</div>
                    {items.map((c, i) => { const ok = c.status === "OK" || c.status === "Good"; return (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", fontSize: 13, padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
                        <span>{c.area}{c.note ? <em style={{ color: "var(--muted)" }}>, {c.note}</em> : ""}</span>
                        <span className="pill-tag" style={{ background: ok ? "#e7ecdf" : c.status === "Needs attention" ? "#f3e4cf" : "#e6e2da", color: ok ? "#5B6440" : c.status === "Needs attention" ? "#8a5a1f" : "#726A5C", flexShrink: 0 }}>{c.status}</span>
                      </div>
                    ); })}
                  </div>
                ))}
              </div>
            )}

            {photos.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <span className="lbl-m">Photos</span>
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  {photos.map((u, i) => <img key={i} src={u} alt="" onClick={() => setLb({ images: photos, index: i })} style={{ width: 110, height: 78, objectFit: "cover", borderRadius: 2, cursor: "pointer" }} />)}
                </div>
              </div>
            )}

            <p style={{ marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--line)", fontSize: 12, color: "var(--muted)", fontStyle: "italic" }} data-testid="report-signoff">Inspected by {r.author}{r.visit_date ? ` on ${r.visit_date}` : ""}. Every checkpoint was physically verified during this visit.</p>

            <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap", alignItems: "center" }}>
              <a className="btn-outline" style={{ padding: "8px 14px" }} href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/${r.report_id}/pdf`} target="_blank" rel="noreferrer" data-testid="report-detail-pdf">Download PDF</a>
              {canManage && <>
                <button className="btn-outline" style={{ padding: "8px 14px", display: "inline-flex", gap: 6, alignItems: "center" }} onClick={resend} disabled={busy} data-testid="report-resend-btn"><Send size={15} /> Resend to client</button>
                <button className="btn-outline" style={{ padding: "8px 14px", display: "inline-flex", gap: 6, alignItems: "center" }} onClick={() => setEditing(true)} data-testid="report-edit-btn"><Pencil size={15} /> Edit</button>
                {r.status !== "Action taken" && <button className="btn-ink" style={{ padding: "8px 14px", display: "inline-flex", gap: 6, alignItems: "center" }} onClick={() => setStatus("Action taken")} data-testid="report-resolve-btn"><CheckCircle2 size={15} /> Mark resolved</button>}
              </>}
            </div>
          </div>
        )}
      </div>
      {lb && <Lightbox images={lb.images} index={lb.index} onClose={() => setLb(null)} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />}
    </div>
  );
}
